import React, { useState, useEffect } from "react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  UserIcon,
  DocumentIcon,
  EyeIcon,
  DownloadIcon,
  XIcon,
  SearchIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/solid";
import { useSidebar } from "../../context/SidebarContext";
import settingsAPI from "../../services/BankingPartnerPortal/settingsAPI";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { bankingPartnerAPI } from "../../services/BankingPartnerPortal/api";
import LoansTable from "../../components/BankingPartnerPortal/LoansTable";
import InsurancesTable from "../../components/BankingPartnerPortal/InsurancesTable";
import AddLoanModal from "../../components/BankingPartnerPortal/AddLoanModal";
import AddInsuranceModal from "../../components/BankingPartnerPortal/AddInsuranceModal";
import { DownloadCloudIcon } from "lucide-react";
const Settings = () => {
  const [activeTab, setActiveTab] = useState("representatives");
  const [representatives, setRepresentatives] = useState([]);
  const [bankInfo, setBankInfo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { sidebarExpanded, isMobile } = useSidebar();
  const [selectedRepresentatives, setSelectedRepresentatives] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRepresentative, setEditingRepresentative] = useState(null);
  const { user } = useAuth();
  const id = user?.id;
  const [complianceDocuments, setComplianceDocuments] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    phone: "",
    email: "",
  });
  // Dynamic state for loans and insurances
  const [loans, setLoans] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [loansLoading, setLoansLoading] = useState(false);
  const [insurancesLoading, setInsurancesLoading] = useState(false);
  const [isAddLoanModalOpen, setIsAddLoanModalOpen] = useState(false);
  const [isAddInsuranceModalOpen, setIsAddInsuranceModalOpen] = useState(false);
  const [isEditBankModalOpen, setIsEditBankModalOpen] = useState(false);
  const [editBankForm, setEditBankForm] = useState({
    bankName: "",
    email: "",
    bankCategory: "",
    branchName: "",
    branchIFSCCode: "",
    bankAddress: "",
  });

  const openEditBankModal = (info) => {
    setEditBankForm({
      bankName: info.bankName || "",
      email: info.email || "",
      bankCategory: info.bankCategory || "",
      branchName: info.branchName || "",
      branchIFSCCode: info.branchIFSCCode || "",
      bankAddress: info.bankAddress || "",
    });
    setIsEditBankModalOpen(true);
  };

  const handleEditBankInfo = (info) => {
    openEditBankModal(info);
  };

  const handleEditBankFormChange = (e) => {
    const { name, value } = e.target;
    setEditBankForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditBankFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await settingsAPI.updateBankInfo(id, editBankForm);
      if (res.success) {
        setBankInfo(res.data || editBankForm);
        setIsEditBankModalOpen(false);
        toast.success(res.message || "Bank information updated successfully");
      } else {
        toast.error(res.message || "Failed to update bank information");
      }
    } catch (err) {
      toast.error(err.message || "Failed to update bank information");
    }
  };

  // Fetch loans and insurances for this banking partner
  const fetchLoansAndInsurances = async () => {
    if (!id) return;
    setLoansLoading(true);
    setInsurancesLoading(true);
    try {
      const res = await bankingPartnerAPI.getById(id);
      const data = res.data || res;
      // Map backend structure to table structure if needed
      setLoans(
        (data.loanDetails || []).map((loan, idx) => ({
          id: idx,
          loanType: loan.loanType,
          minAmount: loan.minLoanAmount,
          maxAmount: loan.maxLoanAmount,
          eligibility: loan.eligibilityCriteria,
          interestRate: loan.interestRate,
          linkedInsurance: loan.loanInsurance,
          status: loan.status || "Active",
        }))
      );
      setInsurances(
        (data.insuranceDetails || []).map((ins, idx) => ({
          id: idx,
          insurance:
            ins.insuranceType +
            (ins.insuranceCategory ? ` / ${ins.insuranceCategory}` : ""),
          eligibility: ins.insuranceEligibility,
          amount: ins.insuranceAmount,
          linkedLoan: ins.linkedLoan || "",
          status: ins.status || "Active",
        }))
      );
    } catch (err) {
      toast.error(err.message || "Failed to fetch loans/insurances");
    } finally {
      setLoansLoading(false);
      setInsurancesLoading(false);
    }
  };

  // Fetch on mount and when activeTab changes to loans/insurances
  useEffect(() => {
    if (activeTab === "loans" || activeTab === "insurances") {
      fetchLoansAndInsurances();
    }
  }, [activeTab, id]);

  const fetchRepresentatives = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getRepresentatives(id);
      setRepresentatives(response.data);
    } catch (err) {
      toast.error(err.message || "Failed to fetch representatives");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data based on active tab
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (activeTab === "representatives") {
          await fetchRepresentatives();
        } else if (activeTab === "documents") {
          // Fetch compliance documents for this banking partner
          const complianceRes = await bankingPartnerAPI.getComplianceDocuments(
            id
          );
          setComplianceDocuments(complianceRes.data || []);
          const [bankInfoResponse, documentsResponse] = await Promise.all([
            settingsAPI.getBankInfo(id),
            settingsAPI.getBankDocuments(id),
          ]);
          setBankInfo(bankInfoResponse.data);
          setDocuments(documentsResponse.data);
        }
      } catch (err) {
        setError(err.message || "Failed to fetch data");
        toast.error(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeTab]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRepresentatives([]);
    } else {
      setSelectedRepresentatives(representatives.map((rep) => rep.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectRepresentative = (id) => {
    if (selectedRepresentatives.includes(id)) {
      setSelectedRepresentatives(
        selectedRepresentatives.filter((repId) => repId !== id)
      );
      setSelectAll(false);
    } else {
      setSelectedRepresentatives([...selectedRepresentatives, id]);
      if (selectedRepresentatives.length + 1 === representatives.length) {
        setSelectAll(true);
      }
    }
  };

  const handleEdit = (representative) => {
    setEditingRepresentative(representative);
    setIsModalOpen(true);
  };

  const handleDelete = async (email) => {
    try {
      const response = await settingsAPI.deleteRepresentative(id, email);
      if (response.success) {
        toast.success("Representative deleted successfully");
        await fetchRepresentatives();
      } else {
        toast.error(response.message || "Failed to delete representative");
        await fetchRepresentatives();
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete representative");
      await fetchRepresentatives();
    }
  };

  const handleAddNew = () => {
    setEditingRepresentative(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingRepresentative(null);
    setFormData({
      name: "",
      designation: "",
      phone: "",
      email: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePhoneNumber = (phone) => {
    // Accepts only 10 digit numbers
    return /^\d{10}$/.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Phone number validation
    if (!validatePhoneNumber(formData.phone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    try {
      let response;
      if (editingRepresentative) {
        response = await settingsAPI.updateRepresentative(id, formData);
        if (response.success) {
          await fetchRepresentatives();
          toast.success("Representative updated successfully");
          setIsModalOpen(false);
          setEditingRepresentative(null);
          setFormData({
            name: "",
            designation: "",
            phone: "",
            email: "",
          });
        } else {
          throw new Error(
            response.message || "Failed to update representative"
          );
        }
      } else {
        response = await settingsAPI.addRepresentative(formData, id);
        if (response.success) {
          await fetchRepresentatives();
          toast.success("Representative added successfully");
          setIsModalOpen(false);
          setFormData({
            name: "",
            designation: "",
            phone: "",
            email: "",
          });
        } else {
          throw new Error(response.message || "Failed to add representative");
        }
      }
    } catch (err) {
      toast.error(err.message || "Failed to save representative");
    } finally {
      setIsModalOpen(false);
      await fetchRepresentatives();
    }
  };

  useEffect(() => {
    if (editingRepresentative) {
      setFormData({
        name: editingRepresentative.name,
        designation: editingRepresentative.designation,
        phone: editingRepresentative.phone,
        email: editingRepresentative.email,
      });
    }
  }, [editingRepresentative]);

  // Handler to update loan status
  const handleLoanStatusChange = async (loan, newStatus, idx) => {
    if (!id) return;
    console.log("Loan status change:", { loan, newStatus, idx, id });
    try {
      await bankingPartnerAPI.updateLoanStatus(id, idx, newStatus);
      toast.success("Loan status updated");
      fetchLoansAndInsurances();
    } catch (err) {
      console.error("Loan status PATCH error:", err);
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to update loan status"
      );
    }
  };

  // Handler to update insurance status
  const handleInsuranceStatusChange = async (ins, newStatus, idx) => {
    if (!id) return;
    console.log("Insurance status change:", { ins, newStatus, idx, id });
    try {
      await bankingPartnerAPI.updateInsuranceStatus(id, idx, newStatus);
      toast.success("Insurance status updated");
      fetchLoansAndInsurances();
    } catch (err) {
      console.error("Insurance status PATCH error:", err);
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to update insurance status"
      );
    }
  };

  // Handler for adding a new loan
  const handleAddLoan = async (loanData) => {
    if (!id) return;
    try {
      await bankingPartnerAPI.addLoan(id, loanData);
      toast.success("Loan added successfully");
      setIsAddLoanModalOpen(false);
      fetchLoansAndInsurances();
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "Failed to add loan"
      );
    }
  };

  // Handler for adding a new insurance
  const handleAddInsurance = async (insuranceData) => {
    if (!id) return;
    try {
      await bankingPartnerAPI.addInsurance(id, insuranceData);
      toast.success("Insurance added successfully");
      setIsAddInsuranceModalOpen(false);
      fetchLoansAndInsurances();
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "Failed to add insurance"
      );
    }
  };

  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [docModalUrl, setDocModalUrl] = useState("");
  const [docModalType, setDocModalType] = useState("");

  const handleViewDocument = (doc) => {
    // Use Cloudinary URL if available, else fallback
    const url =
      doc.cloudinaryUrl || `/uploads/banking-partners/${doc.fileName}`;
    setDocModalUrl(url);
    setDocModalType(doc.type);
    setIsDocModalOpen(true);
  };

  const handleDownloadDocument = (doc) => {
    const url =
      doc.cloudinaryUrl || `/uploads/banking-partners/${doc.fileName}`;
    const link = document.createElement("a");
    link.href = url;
    link.download = `${doc.type || "document"}.jpeg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-red-500 text-center">
          <p className="text-xl font-semibold mb-2">Error Loading Settings</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-black text-white   p-4 mt-12"
      style={{
        marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
        width: isMobile
          ? "100%"
          : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
      }}
    >
      {/* <ToastContainer position="top-right" autoClose={3000} /> */}
      {/* Header */}
      <div className="p-4">
        <h1 className="text-xl font-medium">Settings</h1>
      </div>

      {/* Tabs */}
      <div className="overflow-x-auto scrollbar-hide">
        <div className="flex min-w-max space-x-6 px-2 border-b border-[#333333]">
          <button
            onClick={() => setActiveTab("representatives")}
            className={`tab-button ${
              activeTab === "representatives"
                ? "active-tab-border"
                : "inactive-tab"
            }`}
          >
            Bank Representatives
          </button>
          <button
            onClick={() => setActiveTab("documents")}
            className={`tab-button ${
              activeTab === "documents" ? "active-tab-border" : "inactive-tab"
            }`}
          >
            Documents
          </button>
          <button
            onClick={() => setActiveTab("loans")}
            className={`tab-button ${
              activeTab === "loans" ? "active-tab-border" : "inactive-tab"
            }`}
          >
            Loans
          </button>
          <button
            onClick={() => setActiveTab("insurances")}
            className={`tab-button ${
              activeTab === "insurances" ? "active-tab-border" : "inactive-tab"
            }`}
          >
            Insurances
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 bg-[#131313] rounded-lg mt-4">
        {activeTab === "representatives" && (
          <div>
            {/* Title and Add Button */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <div className="flex items-center">
                <UserIcon className="h-6 w-6 sm:h-5 sm:w-5 text-[#FFCA20] mr-2" />
                <h2 className="text-base sm:text-lg font-medium">
                  Bank Representatives
                </h2>
              </div>
              <button
                onClick={handleAddNew}
                className="bg-[#FFCA20] hover:bg-yellow-600 text-black text-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded flex justify-center sm:justify-start items-center font-medium w-full sm:w-auto"
              >
                <PlusIcon className="h-5 w-5 mr-1" /> Add New Representative
              </button>
            </div>

            {/* Table */}
            <div className="bg-[#131313] rounded-lg p-[-10px] overflow-x-auto scrollbar-hide">
              <table className="min-w-full divide-y divide-gray-800 ">
                <thead className="border border-[#333333]">
                  <tr className="bg-[#131313]">
                    {/* <th className="px-4 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        className=" h-4 w-4 rounded border-[#333333] text-[#FFCA20] focus:ring-yellow-500 "
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th> */}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider border-x border-[#333333] ">
                      Designation
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-x border-[#333333]">
                      Representative
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-x border-[#333333]">
                      Phone Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-x border-[#333333]">
                      Email ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider border-x border-[#333333] rounded-tr-lg">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y border border-white divide-gray-800">
                  {representatives.map((rep) => (
                    <tr key={rep.id} className="hover:bg-gray-800">
                      {/* <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-[#333333] text-[#FFCA20] focus:ring-yellow-500"
                          checked={selectedRepresentatives.includes(rep.id)}
                          onChange={() => handleSelectRepresentative(rep.id)}
                        />
                      </td> */}
                      <td className="px-4 py-3 border-x border-[#333333] text-sm">
                        {rep.designation}
                      </td>
                      <td className="px-4 py-3 border-x border-[#333333]  text-sm">
                        {rep.name}
                      </td>
                      <td className="px-4 py-3 border-x border-[#333333]  text-sm">
                        {rep.phone}
                      </td>
                      <td className="px-4 py-3 border-x border-[#333333]  text-sm">
                        {rep.email}
                      </td>
                      <td className="px-4 py-3 border-x border-[#333333]  text-sm">
                        <div className="flex space-x-2">
                          <button
                            className="text-[#FFCA20] hover:text-[#FFCA20]"
                            onClick={() => handleEdit(rep)}
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            className="text-red-500 hover:text-red-400"
                            onClick={() => handleDelete(rep.email)}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "documents" && bankInfo && (
          <div>
            {/* Bank Information Section */}
            <div className="bg-[#131313] border border-[#333333] rounded-lg p-3 sm:p-5 mb-6">
              <div className="flex justify-between items-start sm:items-center mb-4">
                <div className="flex items-center">
                  <DocumentIcon className="h-5 w-5 text-[#FFCA20] mr-2" />
                  <h2 className="text-lg font-medium text-white">
                    Bank Information
                  </h2>
                </div>
                <button
                  className="border border-[#E6B800] text-[#FFCA20] bg-[#FFCA20]/20 hover:bg-[#FFCA20] hover:text-black transition-colors px-3 py-1 rounded-lg text-sm flex items-center gap-1"
                  onClick={() => handleEditBankInfo(bankInfo)}
                >
                  <PencilIcon className="h-4 w-4" />
                  Edit
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-x-4 sm:gap-x-8 gap-y-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Bank Name</p>
                  <p className="text-sm text-white">
                    {bankInfo.bankName || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Email</p>
                  <p className="text-sm text-white">
                    {bankInfo.email || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Bank Category</p>
                  <p className="text-sm text-white">
                    {bankInfo.bankCategory || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Branch Name</p>
                  <p className="text-sm text-white">
                    {bankInfo.branchName || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Branch IFSC Code</p>
                  <p className="text-sm text-white">
                    {bankInfo.branchIFSCCode || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Bank Address</p>
                  <p className="text-sm text-white">
                    {bankInfo.bankAddress || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Status</p>
                  <p className="text-sm text-white">
                    {bankInfo.status || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Created At</p>
                  <p className="text-sm text-white">
                    {bankInfo.createdAt
                      ? new Date(bankInfo.createdAt).toLocaleString("en-IN")
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Updated At</p>
                  <p className="text-sm text-white">
                    {bankInfo.updatedAt
                      ? new Date(bankInfo.updatedAt).toLocaleString("en-IN")
                      : "N/A"}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-gray-400 mb-1">Loan Management</p>
                  <p className="text-sm text-white">
                    Type: {bankInfo.loanManagement?.types || "N/A"}
                    <br />
                    Interest Rate:{" "}
                    {bankInfo.loanManagement?.interestRate ?? "N/A"}%<br />
                    Min Amount: {bankInfo.loanManagement?.minAmount ?? "N/A"}
                    <br />
                    Max Amount: {bankInfo.loanManagement?.maxAmount ?? "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Bank Verification Documents Section */}
            <div className="bg-[#131313] border border-[#333333] rounded-lg p-5">
              <div className="flex items-center mb-4">
                <DocumentIcon className="h-5 w-5 text-[#FFCA20] mr-2" />
                <h2 className="text-lg font-medium">
                  Bank Verification Documents
                </h2>
              </div>

              <div className="space-y-4">
                {documents.map((doc) => (
                  <div
                    key={doc.type}
                    className="border-b border-[#333333] pb-4"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm mb-1">{doc.type}</p>
                        {doc.cloudinaryUrl || doc.fileName ? (
                          <div className="flex items-center">
                            <span
                              className={`${
                                doc.status === "verified"
                                  ? "bg-green-500 text-black"
                                  : "bg-yellow-500 text-black"
                              } text-xs px-2 py-0.5 rounded-full mr-2`}
                            >
                              {doc.status.charAt(0).toUpperCase() +
                                doc.status.slice(1)}
                            </span>
                            <span className="text-xs text-gray-400">
                              Submitted on{" "}
                              {doc.submittedAt
                                ? new Date(doc.submittedAt).toLocaleDateString()
                                : "-"}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">
                            Not uploaded
                          </span>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        {(doc.cloudinaryUrl || doc.fileName) && (
                          <>
                            <button
                              className="text-[#FFCA20] hover:text-[#FFCA20] flex items-center text-sm"
                              onClick={() => handleViewDocument(doc)}
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              View
                            </button>
                            <button
                              className="text-[#FFCA20] hover:text-[#FFCA20] flex items-center text-sm"
                              onClick={() => handleDownloadDocument(doc)}
                            >
                              <DownloadCloudIcon className="h-4 w-4 mr-1" />
                              Download
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {activeTab === "loans" && (
          <>
            <LoansTable
              loans={loans}
              onEdit={(loan) => {
                /* open edit modal, then fetchLoansAndInsurances() after save */
              }}
              onDelete={(loan) => {
                /* call delete API, then fetchLoansAndInsurances() */
              }}
              onAdd={() => setIsAddLoanModalOpen(true)}
              loading={loansLoading}
              onStatusChange={handleLoanStatusChange}
              getRowKey={(loan) =>
                loan._id ||
                loan.id ||
                `${loan.loanType}-${loan.minAmount}-${loan.maxAmount}`
              }
            />
            <AddLoanModal
              isOpen={isAddLoanModalOpen}
              onClose={() => setIsAddLoanModalOpen(false)}
              onSubmit={handleAddLoan}
              insurances={insurances}
            />
          </>
        )}
        {activeTab === "insurances" && (
          <>
            <InsurancesTable
              insurances={insurances}
              onEdit={(ins) => {
                /* open edit modal, then fetchLoansAndInsurances() after save */
              }}
              onDelete={(ins) => {
                /* call delete API, then fetchLoansAndInsurances() */
              }}
              onAdd={() => setIsAddInsuranceModalOpen(true)}
              loading={insurancesLoading}
              onStatusChange={handleInsuranceStatusChange}
              getRowKey={(ins) =>
                ins._id || ins.id || `${ins.insurance}-${ins.amount}`
              }
            />
            <AddInsuranceModal
              isOpen={isAddInsuranceModalOpen}
              onClose={() => setIsAddInsuranceModalOpen(false)}
              onSubmit={handleAddInsurance}
              loans={loans}
            />
          </>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#131313] rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-medium">
                {editingRepresentative
                  ? "Edit Representative"
                  : "Add New Representative"}
              </h2>
              <button
                onClick={handleModalClose}
                className="text-gray-400 hover:text-white"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-[#333333] rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Designation
                </label>
                <select
                  name="designation"
                  value={formData.designation}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-[#333333] rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                >
                  <option value="">Select Designation</option>
                  <option value="Probationary Officer">
                    Probationary Officer
                  </option>
                  <option value="Loan Officer">Loan Officer</option>
                  <option value="Credit Officer">Credit Officer</option>
                  <option value="Relationship Manage">
                    Relationship Manage
                  </option>
                  <option value="Branch Manager">Branch Manager</option>
                  <option value="Assistant Manager">Assistant Manager</option>
                  <option value="Clerk">Clerk</option>
                  <option value="Specialist Officers">
                    Specialist Officers
                  </option>
                  <option value="Chief/Executive Level Officer">
                    Chief/Executive Level Officer
                  </option>
                  <option value="DSA officer">DSA officer</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-[#333333] rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 border border-[#333333] rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="px-4 py-2 border border-[#333333] rounded-md text-sm hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#FFCA20] text-black rounded-md text-sm hover:bg-yellow-600"
                >
                  {editingRepresentative ? "Update" : "Add"} Representative
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isEditBankModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#131313] rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-medium">Edit Bank Information</h2>
              <button
                onClick={() => setIsEditBankModalOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleEditBankFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Bank Name
                </label>
                <input
                  type="text"
                  name="bankName"
                  value={editBankForm.bankName}
                  onChange={handleEditBankFormChange}
                  className="w-full bg-gray-800 border border-[#333333] rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={editBankForm.email}
                  onChange={handleEditBankFormChange}
                  className="w-full bg-gray-800 border border-[#333333] rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Bank Category
                </label>
                <input
                  type="text"
                  name="bankCategory"
                  value={editBankForm.bankCategory}
                  onChange={handleEditBankFormChange}
                  className="w-full bg-gray-800 border border-[#333333] rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Branch Name
                </label>
                <input
                  type="text"
                  name="branchName"
                  value={editBankForm.branchName}
                  onChange={handleEditBankFormChange}
                  className="w-full bg-gray-800 border border-[#333333] rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Branch IFSC Code
                </label>
                <input
                  type="text"
                  name="branchIFSCCode"
                  value={editBankForm.branchIFSCCode}
                  onChange={handleEditBankFormChange}
                  className="w-full bg-gray-800 border border-[#333333] rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Bank Address
                </label>
                <input
                  type="text"
                  name="bankAddress"
                  value={editBankForm.bankAddress}
                  onChange={handleEditBankFormChange}
                  className="w-full bg-gray-800 border border-[#333333] rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsEditBankModalOpen(false)}
                  className="px-4 py-2 border border-[#333333] rounded-md text-sm hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#FFCA20] text-black rounded-md text-sm hover:bg-yellow-600"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isDocModalOpen && docModalUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-[#131313] rounded-lg p-6 w-full max-w-lg flex flex-col items-center">
            <div className="flex justify-between items-center w-full mb-4">
              <h2 className="text-lg font-medium">{docModalType} Preview</h2>
              <button
                onClick={() => setIsDocModalOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
            <img
              src={docModalUrl}
              alt={docModalType}
              className="max-w-full max-h-[60vh] rounded border border-gray-700"
            />
            <button
              className="mt-4 px-4 py-2 bg-[#FFCA20] text-black rounded hover:bg-yellow-600"
              onClick={() => {
                const link = document.createElement("a");
                link.href = docModalUrl;
                link.download = `${docModalType || "document"}.jpeg`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Download as JPEG
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
